import {useSettingsStore} from "@/store/settings.ts";
import {APP_CONFIG} from "@/config/app.config.ts";

/**
 * Thrown for non-2xx responses. Carries the HTTP status and, when the body is the
 * `{ errorCode, message }` envelope, the raw `errorCode` so callers can branch on a specific
 * failure (e.g. a 403 step-up requirement) instead of parsing the message. A 401 also throws
 * this (with `status === 401`) after scheduling the logout redirect — see `isRedirectingToLogout`.
 */
export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly errorCode: string | null,
        message: string,
        public readonly body: Record<string, unknown> | null = null,
    ) {
        super(message);
        this.name = "ApiError";
    }
}

// Once a 401 has started the logout redirect, this stays set for the rest of the page's life
// (module state resets on the next full navigation). It de-dups the redirect — without it a
// background poller hitting 401 every few seconds would queue a fresh `window.location` each
// tick — and lets background loops stop instead of firing requests behind the redirect.
let redirectingToLogout = false;

/** True once a 401 has started the logout redirect. Background loops (pollers) should stop when set. */
export function isRedirectingToLogout(): boolean {
    return redirectingToLogout;
}

/**
 * A pre-configured fetch client that handles URL construction,
 * default headers, and consistent error handling.
 */
export const apiClient = {
    async fetch<T>(path: string, options?: RequestInit): Promise<T> {
        const settings = useSettingsStore();
        const baseUrl = APP_CONFIG.API_ENDPOINT;

        // Construct the full URL by combining the base URL and the endpoint.
        // baseUrl can be either a URL object or a string (for relative paths)
        const fullUrl = new URL(`/api/app/${path}`, baseUrl instanceof URL ? baseUrl.href : baseUrl).href;

        const defaultHeaders = {
            'Content-Type': 'application/json',
            'Accept-Language': settings.cultureInfo
        };

        const finalOptions: RequestInit = {
            ...options,
            headers: {
                ...defaultHeaders,
                ...options?.headers,
            },
        };

        const response = await fetch(fullUrl, finalOptions);

        if (!response.ok) {
            if (response.status === 401) {
                if (!redirectingToLogout) {
                    redirectingToLogout = true;
                    setTimeout(() => {
                        window.location.href = '/Authent/Logout';
                    }, 2000);
                }
                throw new ApiError(401, null, 'Unauthorized - redirecting to logout');
            }

            let errorCode: string | null = null;
            let message = `HTTP Error: ${response.status} ${response.statusText}`;
            let errorBody: Record<string, unknown> | null = null;
            try {
                const body = await response.json();
                if (body && typeof body.errorCode === 'string') {
                    errorCode = body.errorCode;
                    errorBody = body;
                    if (body.message) message = body.message;
                }
            } catch {
                // Non-JSON body — keep the status-line message.
            }
            throw new ApiError(response.status, errorCode, message, errorBody);
        }

        // Handle responses that don't have a body (e.g., HTTP 204 No Content).
        if (response.status === 204) {
            return null as T;
        }

        return response.json();
    }
};
